import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, CheckCircle2, Clock, Eye, Link2, Loader2, Plus, RotateCcw, Save, Send, Trash2, X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  ivrPublishService, blockingIssues, countdown, localDiff, newState, parseExceptions, parseJsonField,
  type AttachCheck, type Draft, type FlowDetail, type Issue, type PublishResult, type StateRow, type VersionSummary,
} from '../../api/services/ivrPublish.service';
import { numberLifecycleService } from '../../api/services/numberLifecycle.service';

/**
 * One IVR app (operator/service): versioned drafts per language, validation, a
 * field-level preview against the live switch, publish, rollback (republish an older
 * version), pending deletions and — for Green Giraffe — attaching it to a number.
 * docs/design/2026-10-14_ivr_app_management.md, Phases 3-4 + self-service.
 *
 * A business admin reaches this only for a flow filed to their organisation whose
 * every number is theirs; the backend enforces it. The recording folder is locked.
 */
const CONTEXTS = ['dtmf', 'stream', 'endcall', 'transfer', 'voice_mail', 'alert', 'polling'];
const ACTIONS = ['static', 'api_call', 'function_call'];

const IssueList: React.FC<{ issues: Issue[] }> = ({ issues }) => (
  <ul className="text-xs space-y-0.5">
    {issues.map((i, k) => (
      <li key={k} className={i.severity === 'ERROR' ? 'text-red-700' : 'text-amber-700'}>
        <strong>{i.severity === 'ERROR' ? 'Error' : 'Warning'}</strong> · {i.state} · {i.code}: {i.message}
      </li>
    ))}
  </ul>
);

const PreviewPanel: React.FC<{ result: PublishResult }> = ({ result }) => (
  <div className="space-y-3">
    {result.notPublishedToReason && <p className="text-xs text-gray-600">{result.notPublishedToReason}</p>}
    {result.targets.map(t => (
      <div key={t.providerId} className="border border-gray-200 rounded-lg p-3 text-xs space-y-1.5">
        <div className="font-medium text-sm">{t.provider}</div>
        <div>
          {t.inserts} new · {t.updates} changed · {t.unchanged} unchanged · {t.deletesAfterGrace.length} removed
          {' '}(deleted {result.deleteGraceMinutes} min after publish) · {t.statesBefore} → {t.statesAfter} states
        </div>
        <div>
          Recording folder: <span className="font-mono">{t.businessName.before ?? '—'}</span>
          {t.businessName.change ? <> → <span className="font-mono text-red-700">{t.businessName.after}</span></> : ' (kept)'}
          {t.businessName.locked && ' · locked'}
        </div>
        <div className="text-gray-500">call_request column: {t.callRequest.column} ({t.callRequest.evidence})</div>
        {t.otherLanguagesUntouched.length > 0 && (
          <div className="text-gray-500">Other languages untouched: {t.otherLanguagesUntouched.join(', ')}</div>
        )}
        {t.writes.filter(w => w.action !== 'UNCHANGED').map(w => (
          <div key={`${w.name}|${w.callRequest}`} className="border-t pt-1">
            <span className={w.action === 'INSERT' ? 'text-green-700' : 'text-blue-700'}>{w.action}</span>{' '}
            <span className="font-mono">{w.name}</span>{w.callRequest ? ` (${w.callRequest})` : ''}
            {Object.entries(w.diff || {}).map(([f, d]) => (
              <div key={f} className="font-mono text-[11px] pl-3 break-all">
                {f}: <span className="text-red-700">{String(d.before ?? '∅')}</span> → <span className="text-green-700">{String(d.after ?? '∅')}</span>
              </div>
            ))}
          </div>
        ))}
        {t.deletesAfterGrace.length > 0 && (
          <div className="border-t pt-1 text-red-700">
            Removed (after the grace): {t.deletesAfterGrace.map(d => `${d.name}${d.alreadyScheduled ? ' (already scheduled)' : ''}`).join(', ')}
          </div>
        )}
        {t.pendingDeletionsCancelled.length > 0 && (
          <div className="text-green-700">Put back (pending deletion cancelled): {t.pendingDeletionsCancelled.join(', ')}</div>
        )}
        {t.validation.length > 0 && <IssueList issues={t.validation} />}
        {t.warnings.map((w, i) => <div key={i} className="text-amber-700">{w}</div>)}
        {t.blockers.map((b, i) => <div key={i} className="text-red-700 font-medium">Blocked: {b}</div>)}
      </div>
    ))}
    {result.results && result.results.map(r => (
      <div key={r.pushId} className="text-xs bg-green-50 border border-green-200 rounded p-2">
        {r.provider}: {r.result} · publish #{r.pushId} · {r.rowsWritten} row(s) written
        {r.versionNo ? ` · version ${r.versionNo}` : ''}
        {r.deletionsScheduled.length > 0 && ` · ${r.deletionsScheduled.length} state(s) deleted after ${result.deleteGraceMinutes} min`}
        {r.failures.map((f, i) => <div key={i} className="text-red-700">{f}</div>)}
      </div>
    ))}
    {result.applied && !result.delayedDeleteJobEnabled && result.results?.some(r => r.deletionsScheduled.length) && (
      <p className="text-xs text-amber-700">
        The deletion job is off on this server: removed states stay until Green Giraffe finalizes them.
      </p>
    )}
  </div>
);

const IvrAppEditor: React.FC = () => {
  const [params] = useSearchParams();
  const op = params.get('op') || '';
  const svc = params.get('svc') || '';
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;

  const [detail, setDetail] = useState<FlowDetail | null>(null);
  const [lang, setLang] = useState<string>('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [rows, setRows] = useState<StateRow[]>([]);
  const [baseRows, setBaseRows] = useState<StateRow[]>([]);
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [preview, setPreview] = useState<PublishResult | null>(null);
  const [rollback, setRollback] = useState<{ version: VersionSummary; result: PublishResult } | null>(null);
  const [viewing, setViewing] = useState<{ version: VersionSummary; states: StateRow[] } | null>(null);
  const [exceptions, setExceptions] = useState('');
  const [ackDrift, setAckDrift] = useState(false);
  const [folder, setFolder] = useState('');
  const [confirmFolder, setConfirmFolder] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [newLang, setNewLang] = useState('');

  const load = useCallback(async () => {
    setError(null);
    const r = await ivrPublishService.flow(op, svc, orgId);
    if (!r.success || !r.data) { setError(r.error || 'Could not load'); return; }
    setDetail(r.data);
    setTick(0);
    setLang(l => l || r.data!.languages[0] || 'en');
  }, [op, svc, orgId]);

  useEffect(() => { if (op && svc) load(); }, [load, op, svc]);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const openDraft = detail?.drafts.find(d => d.status === 'OPEN' && d.language === lang);

  useEffect(() => {
    setDraft(null); setRows([]); setBaseRows([]); setIssues(null); setPreview(null);
    if (!openDraft) return;
    (async () => {
      const r = await ivrPublishService.draft(openDraft.id, orgId);
      if (!r.success || !r.data) { setError(r.error || 'Could not load the draft'); return; }
      setDraft(r.data);
      setRows(r.data.states);
      if (r.data.baseVersionId) {
        const v = await ivrPublishService.version(r.data.baseVersionId, orgId);
        if (v.success && v.data) setBaseRows(v.data.states);
      }
    })();
  }, [openDraft?.id, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async <T,>(label: string, fn: () => Promise<{ success: boolean; error?: string; data?: T }>,
                        after?: (d: T) => void) => {
    setBusy(label); setError(null);
    const r = await fn();
    setBusy(null);
    if (!r.success) { setError(r.error || `${label} failed`); return; }
    after?.(r.data as T);
  };

  const dirty = useMemo(() => draft != null && JSON.stringify(draft.states) !== JSON.stringify(rows), [draft, rows]);
  const diff = useMemo(() => localDiff(baseRows, rows), [baseRows, rows]);
  const jsonErrors = useMemo(() => {
    const out: string[] = [];
    rows.forEach(r => {
      (['action_details', 'type_meta'] as const).forEach(f => {
        const p = parseJsonField(r[f]);
        if (!p.ok) out.push(`${r.name}.${f}: ${p.error}`);
      });
    });
    return out;
  }, [rows]);

  const setRow = (i: number, patch: Partial<StateRow>) => {
    setRows(rs => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
    setPreview(null); setIssues(null);
  };

  const publishOpts = (apply: boolean) => ({
    apply,
    languageExceptions: parseExceptions(exceptions),
    organizationId: orgId,
    ...(detail?.master ? {
      acknowledgeDrift: ackDrift,
      businessName: folder.trim() || undefined,
      confirmBusinessNameChange: confirmFolder,
    } : {}),
  });

  const versionsHere = (detail?.versions || []).filter(v => v.language === lang);
  const pending = (detail?.pendingDeletions || []).filter(d => d.status === 'PENDING');

  if (!op || !svc) return <p className="text-sm text-gray-500">No IVR app chosen.</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/telephony/ivr/switch-sync" className="text-xs text-blue-700 flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> {detail?.master ? 'IVR switch sync' : 'My IVR apps'}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-mono">{op}/{svc}</h1>
          {detail && (
            <p className="text-xs text-gray-600">
              Owner: {detail.ownership.ownerOrgName || detail.ownership.ownerOrgId || detail.ownership.status}
              {' '}· publishes to {detail.homeProviders.map(p => p.name).join(', ') || '(the switch it came from)'}
              {detail.manageReason && !detail.master ? ` · ${detail.manageReason}` : ''}
            </p>
          )}
        </div>
      </div>

      {busy && <p className="text-sm text-gray-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {busy}…</p>}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex gap-2" role="alert">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}

      {detail && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Language:</span>
            {detail.languages.map(l => (
              <button key={l} onClick={() => setLang(l)}
                className={`px-2 py-1 rounded border ${l === lang ? 'border-blue-600 text-blue-700' : 'border-gray-300'}`}>
                {l || '(none)'}
              </button>
            ))}
            <input className="tl-input w-24" placeholder="new: fr" value={newLang} onChange={e => setNewLang(e.target.value.trim())} />
            <button className="text-xs text-blue-700 disabled:opacity-50" disabled={!newLang || !!busy}
              onClick={() => run('Open draft', () => ivrPublishService.createDraft({
                operatorId: op, serviceId: svc, language: newLang, copyFromLanguage: lang, organizationId: orgId,
              }), () => { setLang(newLang); setNewLang(''); load(); })}>
              <Plus className="w-3 h-3 inline" /> Add language (copy of {lang})
            </button>
          </div>

          {/* ── draft editor ── */}
          <section className="tl-card p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold">
                {draft ? `Draft #${draft.id} · ${lang} · revision ${draft.revision}` : `Edit ${lang}`}
              </h2>
              {!draft && (
                <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={!!busy}
                  onClick={() => run('Open draft', () => ivrPublishService.createDraft({
                    operatorId: op, serviceId: svc, language: lang, organizationId: orgId,
                  }), () => load())}>
                  Open a draft of the live version
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500">
              A draft is a copy of the latest version; nothing reaches callers until you publish it. The recording
              folder (business_name) is locked{detail.master ? ' — change it only with the publish option below' : ''}.
            </p>

            {draft && (
              <>
                <div className="text-xs text-gray-600">
                  Against the live version: {diff.added.length} added{diff.added.length ? ` (${diff.added.join(', ')})` : ''},
                  {' '}{diff.changed.length} changed{diff.changed.length ? ` (${diff.changed.join(', ')})` : ''},
                  {' '}{diff.removed.length} removed{diff.removed.length ? ` (${diff.removed.join(', ')})` : ''}
                  {dirty && <span className="text-amber-700"> · unsaved changes</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1 pr-2">State</th>
                        <th className="py-1 pr-2">Direction</th>
                        <th className="py-1 pr-2">Action · context</th>
                        <th className="py-1 pr-2">action_details (JSON)</th>
                        <th className="py-1 pr-2">type_meta (JSON)</th>
                        <th className="py-1" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="border-b align-top">
                          <td className="py-1 pr-2">
                            <input className="tl-input font-mono w-32" value={r.name}
                              onChange={e => setRow(i, { name: e.target.value })} />
                            {r.is_blocked ? <div className="text-amber-700">blocked</div> : null}
                          </td>
                          <td className="py-1 pr-2">
                            <select className="tl-input" value={r.call_request ?? ''}
                              onChange={e => setRow(i, { call_request: e.target.value || null })}>
                              <option value="">(none)</option>
                              <option value="inbound">inbound</option>
                              <option value="outbound">outbound</option>
                            </select>
                          </td>
                          <td className="py-1 pr-2 space-y-1">
                            <select className="tl-input" value={r.action ?? ''} onChange={e => setRow(i, { action: e.target.value })}>
                              {!ACTIONS.includes(r.action ?? '') && <option value={r.action ?? ''}>{r.action || '—'}</option>}
                              {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                            </select>
                            <input className="tl-input" list="ivr-contexts" value={r.context_type ?? ''}
                              onChange={e => setRow(i, { context_type: e.target.value })} />
                          </td>
                          <td className="py-1 pr-2">
                            <textarea className="tl-input font-mono text-[11px] w-64 h-20" value={r.action_details ?? ''}
                              onChange={e => setRow(i, { action_details: e.target.value })} />
                          </td>
                          <td className="py-1 pr-2">
                            <textarea className="tl-input font-mono text-[11px] w-72 h-20" value={r.type_meta ?? ''}
                              onChange={e => setRow(i, { type_meta: e.target.value || null })} />
                          </td>
                          <td className="py-1">
                            <button className="text-red-700" title="Remove state (deleted 10 min after publish)"
                              onClick={() => { setRows(rs => rs.filter((_, k) => k !== i)); setPreview(null); }}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <datalist id="ivr-contexts">{CONTEXTS.map(c => <option key={c} value={c} />)}</datalist>
                </div>
                {jsonErrors.length > 0 && <div className="text-xs text-red-700">{jsonErrors.join(' · ')}</div>}
                <div className="flex flex-wrap gap-2">
                  <button className="tl-btn tl-btn-secondary tl-btn-sm"
                    onClick={() => setRows(rs => [...rs, newState(`STATE_${rs.length + 1}`, lang, rs[0]?.call_request ?? 'inbound')])}>
                    <Plus className="w-3.5 h-3.5" /> Add state
                  </button>
                  <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={!dirty || !!busy || jsonErrors.length > 0}
                    onClick={() => run('Save', () => ivrPublishService.saveDraft(draft.id, draft.revision, rows, undefined, orgId),
                      d => { setDraft(d); setRows(d.states); })}>
                    <Save className="w-3.5 h-3.5" /> Save draft
                  </button>
                  <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={dirty || !!busy}
                    onClick={() => run('Validate', () => ivrPublishService.validate(draft.id, orgId), d => setIssues(d.issues))}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Validate
                  </button>
                  <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={dirty || !!busy}
                    onClick={() => run('Preview', () => ivrPublishService.publish(draft.id, publishOpts(false)), setPreview)}>
                    <Eye className="w-3.5 h-3.5" /> Preview diff
                  </button>
                  <button className="px-3 py-1.5 border border-gray-300 rounded text-xs text-red-700" disabled={!!busy}
                    onClick={() => { if (window.confirm('Discard this draft?')) run('Discard', () => ivrPublishService.discardDraft(draft.id, orgId), () => load()); }}>
                    <X className="w-3.5 h-3.5 inline" /> Discard
                  </button>
                </div>
                {dirty && <p className="text-xs text-gray-500">Save before validating or previewing.</p>}
                {issues && (issues.length ? <IssueList issues={issues} /> : <p className="text-xs text-green-700">No problems found offline.</p>)}

                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <label>Language exceptions{' '}
                    <input className="tl-input w-48 inline-block" placeholder="state names, or *" value={exceptions}
                      onChange={e => { setExceptions(e.target.value); setPreview(null); }} />
                  </label>
                  {detail.master && (
                    <>
                      <label><input type="checkbox" checked={ackDrift} onChange={e => { setAckDrift(e.target.checked); setPreview(null); }} /> publish over a hand edit (drift)</label>
                      <label>New recording folder{' '}
                        <input className="tl-input w-32 inline-block" value={folder} onChange={e => { setFolder(e.target.value); setPreview(null); }} />
                      </label>
                      {folder && <label><input type="checkbox" checked={confirmFolder} onChange={e => setConfirmFolder(e.target.checked)} /> I confirm the folder change</label>}
                    </>
                  )}
                </div>

                {preview && (
                  <div className="space-y-2">
                    <PreviewPanel result={preview} />
                    {preview.applyEnabled === false && !preview.applied && (
                      <div className="text-xs text-amber-800">
                        Preview only: writing flows to the switch is switched off (mode: {preview.mode}). Nothing can be published yet.
                      </div>
                    )}
                    {!preview.applied && (
                      <button className="tl-btn tl-btn-primary tl-btn-sm"
                        disabled={!preview.publishable || !!busy || preview.applyEnabled === false
                          || blockingIssues(preview.targets.flatMap(t => t.validation)).length > 0}
                        title={preview.applyEnabled === false ? `Publishing is switched off (mode: ${preview.mode})` : undefined}
                        onClick={() => {
                          if (!window.confirm(`Publish ${op}/${svc} ${lang} to ${preview.targets.map(t => t.provider).join(', ')}?`)) return;
                          run('Publish', () => ivrPublishService.publish(draft.id, { ...publishOpts(true), previewHash: preview.previewHash }),
                            d => { setPreview(d); load(); });
                        }}>
                        <Send className="w-3.5 h-3.5" /> Publish
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </section>

          {/* ── pending deletions ── */}
          {pending.length > 0 && (
            <section className="tl-card p-4 space-y-2">
              <h2 className="font-semibold flex items-center gap-1"><Clock className="w-4 h-4" /> Pending deletions</h2>
              <p className="text-xs text-gray-500">
                Removed states stay on the switch for {detail.deleteGraceMinutes} minutes so calls in them finish.
                {!detail.delayedDeleteJobEnabled && ' The deletion job is off on this server; Green Giraffe finalizes them.'}
              </p>
              <ul className="text-xs space-y-0.5">
                {pending.map(d => (
                  <li key={d.id}>
                    <span className="font-mono">{d.name}</span> · {d.language}{d.callRequest ? `/${d.callRequest}` : ''} · publish #{d.pushId}
                    {' '}· {countdown(d.secondsLeft == null ? null : d.secondsLeft - tick)}
                    {detail.master && (
                      <button className="ml-2 text-blue-700" disabled={!!busy}
                        onClick={() => run('Finalize', () => ivrPublishService.finalizeDeletions(d.pushId), () => load())}>
                        Finalize deletions of #{d.pushId} now
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── version history + rollback ── */}
          <section className="tl-card p-4 space-y-2">
            <h2 className="font-semibold">Version history · {lang}</h2>
            {rollback && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <div className="text-sm font-medium">
                  Roll back to version {rollback.version.versionNo} on {rollback.version.provider}
                  {rollback.result.applied ? ' — done' : ' — preview, nothing written'}
                </div>
                <PreviewPanel result={rollback.result} />
                <div className="flex gap-2">
                  {!rollback.result.applied && (
                    <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={!rollback.result.publishable || !!busy}
                      onClick={() => run('Rollback', () => ivrPublishService.republish(rollback.version.versionId,
                        { ...publishOpts(true), previewHash: rollback.result.previewHash }),
                        d => { setRollback({ version: rollback.version, result: d }); load(); })}>
                      <RotateCcw className="w-3.5 h-3.5" /> Republish this version
                    </button>
                  )}
                  <button className="px-3 py-1.5 border border-gray-300 rounded text-xs" onClick={() => setRollback(null)}>Close</button>
                </div>
              </div>
            )}
            {viewing && (
              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[11px] font-mono max-h-64 overflow-auto">
                <div className="flex justify-between mb-1 font-sans text-xs">
                  <span>Version {viewing.version.versionNo} · {viewing.version.provider} · {viewing.states.length} states</span>
                  <button onClick={() => setViewing(null)}><X className="w-3 h-3" /></button>
                </div>
                {viewing.states.map(s => (
                  <div key={`${s.name}|${s.call_request}`}>{s.name} [{s.call_request ?? '—'}] {s.action}/{s.context_type} {s.type_meta ?? ''}</div>
                ))}
              </div>
            )}
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 pr-2">Switch</th><th className="py-1 pr-2">Version</th><th className="py-1 pr-2">Source</th>
                  <th className="py-1 pr-2">When · who</th><th className="py-1 pr-2">States</th><th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {versionsHere.map((v, i) => {
                  const latestOnSwitch = versionsHere.findIndex(x => x.providerId === v.providerId) === i;
                  return (
                    <tr key={v.versionId} className="border-b">
                      <td className="py-1 pr-2">{v.provider || v.providerId}</td>
                      <td className="py-1 pr-2">v{v.versionNo}{latestOnSwitch ? ' (live)' : ''}</td>
                      <td className="py-1 pr-2">{v.source}</td>
                      <td className="py-1 pr-2">{v.createdAt?.replace('T', ' ').slice(0, 16)} · {v.createdBy || '—'}</td>
                      <td className="py-1 pr-2">{v.stateCount}</td>
                      <td className="py-1 space-x-2">
                        <button className="text-blue-700" onClick={() => run('Load version', () => ivrPublishService.version(v.versionId, orgId),
                          d => setViewing({ version: v, states: d.states }))}>View</button>
                        {!latestOnSwitch && (
                          <button className="text-blue-700" disabled={!!busy}
                            onClick={() => run('Rollback preview', () => ivrPublishService.republish(v.versionId, publishOpts(false)),
                              d => setRollback({ version: v, result: d }))}>
                            Roll back to this…
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {versionsHere.length === 0 && <p className="text-xs text-gray-500">No version of {lang} yet.</p>}
          </section>

          {/* ── publishes ── */}
          <section className="tl-card p-4 space-y-2">
            <h2 className="font-semibold">Publishes</h2>
            <ul className="text-xs space-y-0.5">
              {detail.publishes.map(p => (
                <li key={p.id}>
                  #{p.id} {p.kind} · {p.provider} · {p.language} · {p.result} · {p.rowsWritten ?? 0} written, {p.rowsDeleted ?? 0} deleted
                  {' '}· {p.pushedAt?.replace('T', ' ').slice(0, 16)} · {p.pushedBy}
                </li>
              ))}
              {detail.publishes.length === 0 && <li className="text-gray-500">Nothing published yet.</li>}
            </ul>
          </section>

          {detail.master && <AttachPanel op={op} svc={svc} language={lang} />}
        </>
      )}
    </div>
  );
};

/**
 * Phase 4: attach this app to a number. The write is the number-lifecycle push (it sets
 * operator_id/service_id on the number's home switch) — this panel only adds the check
 * that the flow is on that switch in the right language. Green Giraffe only, like the
 * lifecycle itself.
 */
const AttachPanel: React.FC<{ op: string; svc: string; language: string }> = ({ op, svc, language }) => {
  const [numberId, setNumberId] = useState('');
  const [check, setCheck] = useState<(AttachCheck & { e164?: string }) | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doCheck = async () => {
    setBusy(true); setError(null); setCheck(null); setPreview(null); setResult(null);
    const d = await numberLifecycleService.details(Number(numberId));
    if (!d.success || !d.data) { setBusy(false); setError(d.error || 'No such number'); return; }
    const n = d.data.number as any;
    const c = await ivrPublishService.attachCheck(op, svc, n.provider_id, language);
    if (c.success && c.data) setCheck({ ...c.data, e164: n.e164 }); else setError(c.error || 'Check failed');
    const p = await numberLifecycleService.pushPreview(Number(numberId), { operatorId: op, serviceId: svc });
    setBusy(false);
    if (p.success) setPreview(p.data); else setError(p.error || 'Preview refused');
  };
  const doApply = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    const r = await numberLifecycleService.pushApply(Number(numberId),
      { operatorId: op, serviceId: svc, previewHash: preview.previewHash });
    setBusy(false);
    if (r.success) { setResult(r.data); setPreview(null); } else setError(r.error || 'Refused');
  };
  const plan = preview?.plans?.[0];

  return (
    <section className="tl-card p-4 space-y-2">
      <h2 className="font-semibold flex items-center gap-1"><Link2 className="w-4 h-4" /> Attach to a number</h2>
      <p className="text-xs text-gray-500">
        Points a number at this app on the number&rsquo;s home switch, through the number lifecycle push (previewed).
        The number id is on Number Ownership.
      </p>
      <div className="flex gap-2">
        <input className="tl-input w-40" placeholder="number id" value={numberId} onChange={e => setNumberId(e.target.value.replace(/\D/g, ''))} />
        <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={!numberId || busy} onClick={doCheck}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Check + preview
        </button>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      {check && (
        <div className={`text-xs ${check.ready ? 'text-green-700' : 'text-amber-700'}`}>
          {check.e164}: flow {check.presentOnSwitch ? `on the switch in ${check.languagesOnSwitch.join(', ')}` : 'NOT on the switch'}
          {check.openDrafts.length ? ` · unpublished drafts: ${check.openDrafts.join(', ')}` : ''}
          {check.notes.map((n, i) => <div key={i}>{n}</div>)}
        </div>
      )}
      {plan && (
        <div className="text-xs">
          Switch plan: {plan.action}{plan.reason ? ` — ${plan.reason}` : ''}
          {(plan.warnings || []).map((w: string, i: number) => <div key={i} className="text-amber-700">{w}</div>)}
        </div>
      )}
      {preview && (
        <button className="tl-btn tl-btn-primary tl-btn-sm" onClick={doApply}
          disabled={busy || preview.mode !== 'APPLY' || !plan || !['ASSIGN', 'CREATE'].includes(plan.action)}>
          <Send className="w-3.5 h-3.5" /> Attach
        </button>
      )}
      {preview && preview.mode !== 'APPLY' && <p className="text-xs text-amber-700">Switch push is {String(preview.mode).toLowerCase()}: apply is disabled on the server.</p>}
      {result && (result.results || []).map((x: any) => (
        <p key={x.pushId || x.e164} className="text-xs">{x.action} · {x.status}{x.pushId ? ` (push #${x.pushId})` : ''} — {x.detail}</p>
      ))}
    </section>
  );
};

export default IvrAppEditor;
